import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { renderDefaultEditor } from './Editor'
import AiDraftSuccessScreen from './SuccessScreen'
import AiDraftErrorDisplay from './ErrorDisplay'
import { WORKFLOW_CONSTANTS } from '../../../models/aiAnalysis/workflow'
import type { DraftAction, CardState } from '../../../models/aiAnalysis/workflow'
import type { AiDraftEditorProps, AiDraftWorkflowOrchestratorProps, EditedPayload, RenderEditor } from './types'
import { Requests, ReactQuery } from '../../../api'

import { classifyAiActionError, shouldCloseOnDismiss } from '../../../utils/aiAnalysis/actionErrors'
import { getProvenanceHeaderText } from '../../../utils/aiAnalysis/provenanceHeader'
import styles from './styles.module.scss'

// buildActions already yields this order; the sort keeps rendering order
// independent of the caller.
const CARD_ORDER: Record<DraftAction['action_type'], number> = {
  forward: 0,
  acknowledge: 1,
  combined_to_client: 2,
  combined_to_notary: 3
}

const sortActions = (actions: DraftAction[]): DraftAction[] =>
  [...actions].sort((a, b) => CARD_ORDER[a.action_type] - CARD_ORDER[b.action_type])

interface AiDraftCardProps {
  action: DraftAction
  onDecided: (actionId: string) => void
  onFatalDismiss?: () => void
  autoFocus: boolean
  renderEditor?: RenderEditor
}

// Owns a single pending action end-to-end: its own edit buffer, its own
// approve/decline mutations, and its own post-decision state. Deciding this
// card never touches any other card rendered alongside it.
const AiDraftCard: React.FC<AiDraftCardProps> = ({ action, onDecided, onFatalDismiss, autoFocus, renderEditor }) => {
  const [cardState, setCardState] = useState<CardState>('draft')
  const [text, setText] = useState<string>(action.action_payload.text ?? '')
  const [retryCount, setRetryCount] = useState<number>(0)
  const [declineError, setDeclineError] = useState<unknown>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (autoFocus && containerRef.current) {
      containerRef.current.focus()
    }
  }, [autoFocus])

  const approveMutation = useMutation({
    mutationFn: async ({
      actionId,
      decision,
      text: submittedText
    }: {
      actionId: string
      decision: 'approved' | 'edited'
      text?: string
    }) => {
      const request = Requests.updateAiAction({ actionId, decision, text: submittedText })
      return await ReactQuery.patchResource(request, request.data)
    },
    onSuccess: () => {
      setRetryCount(0)
      setCardState('sent')
      onDecided(action.id)
    },
    onError: () => {
      setRetryCount((prev) => prev + 1)
    },
    useErrorBoundary: false
  })

  const handleApprove = async (
    actionId: string,
    decision: 'approved' | 'edited',
    editedPayload: EditedPayload | null
  ) => {
    const submittedText = decision === 'edited' ? editedPayload?.text ?? text : undefined

    try {
      await approveMutation.mutateAsync({ actionId, decision, text: submittedText })
    } catch (error) {
      // Handled by mutation's onError callback
    }
  }

  const declineMutation = useMutation({
    mutationFn: async (actionId: string) => {
      const request = Requests.updateAiAction({ actionId, decision: 'declined' })
      return await ReactQuery.patchResource(request, request.data)
    },
    useErrorBoundary: false
  })

  const handleDecline = async () => {
    setDeclineError(null)
    try {
      await declineMutation.mutateAsync(action.id)
      setCardState('declined')
      onDecided(action.id)
      return true
    } catch (err) {
      setDeclineError(err)
      setRetryCount((prev) => prev + 1)
      return false
    }
  }

  const currentError = declineError ?? approveMutation.error
  const classifiedError = currentError ? classifyAiActionError(currentError, retryCount) : null

  const handleDismissError = async () => {
    if (classifiedError && shouldCloseOnDismiss(classifiedError.type)) {
      // Dismissing on ALREADY_DECIDED or FATAL leaves this card undecided —
      // only the explicit Decline button decides it. Recovery belongs to the
      // host.
      onFatalDismiss?.()
    } else {
      setDeclineError(null)
      approveMutation.reset()
    }
  }

  if (cardState === 'sent') {
    return (
      <div className={styles.cardDecidedIndicator} data-test="card-decided-sent" role="status" aria-live="polite">
        Sent
      </div>
    )
  }

  if (cardState === 'declined') {
    return (
      <div className={styles.cardDecidedIndicator} data-test="card-decided-declined" role="status" aria-live="polite">
        Declined
      </div>
    )
  }

  if (classifiedError) {
    return (
      <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="Error">
        <AiDraftErrorDisplay
          error={classifiedError.message}
          errorType={classifiedError.type}
          onDismiss={handleDismissError}
        />
      </div>
    )
  }

  const editorProps: AiDraftEditorProps = {
    action,
    onApprove: handleApprove,
    onDecline: handleDecline,
    currentText: text,
    onTextChange: setText
  }

  return (
    <div className={styles.workflowContainer} ref={containerRef} tabIndex={-1} aria-label="AI draft workflow">
      <React.Fragment key={`${action.id}-${action.action_type}`}>
        {(renderEditor ?? renderDefaultEditor)(editorProps)}
      </React.Fragment>
    </div>
  )
}

const AiDraftWorkflowOrchestrator: React.FC<AiDraftWorkflowOrchestratorProps> = ({
  actions,
  isOpen,
  onClose,
  onFatalDismiss,
  displayTimeZone,
  autoFocus = true,
  renderEditor,
  showProvenanceHeader = true
}) => {
  const sortedActions = sortActions(actions)
  const [decidedIds, setDecidedIds] = useState<Set<string>>(new Set())
  const [allDecided, setAllDecided] = useState<boolean>(false)

  const handleCardDecided = useCallback(
    (actionId: string) => {
      setDecidedIds((prev) => {
        const next = new Set(prev)
        next.add(actionId)
        return next
      })
    },
    [setDecidedIds]
  )

  const allActionsDecided = sortedActions.length > 0 && sortedActions.every((action) => decidedIds.has(action.id))

  useEffect(() => {
    if (!allActionsDecided) return undefined

    setAllDecided(true)

    // A single-card popover closes immediately, matching the pre-cards
    // behavior for a lone pending action. A multi-card popover shows a brief
    // "all done" confirmation before closing.
    if (sortedActions.length <= 1) {
      onClose()
      return undefined
    }

    const timer = setTimeout(() => {
      onClose()
    }, WORKFLOW_CONSTANTS.TRANSITION_DELAY_MS)

    return () => clearTimeout(timer)
  }, [allActionsDecided, sortedActions.length, onClose])

  if (!isOpen || sortedActions.length === 0) {
    return null
  }

  if (allDecided && sortedActions.length > 1) {
    return (
      <div className={styles.workflowContainer} tabIndex={-1} aria-label="Success confirmation">
        <AiDraftSuccessScreen message={WORKFLOW_CONSTANTS.ALL_CARDS_DECIDED_MESSAGE} subtitle="" />
      </div>
    )
  }

  // Single header for the whole popover, not per card. Absent entirely (no element
  // rendered, no reserved space) when no combined action in the payload
  // carries absorbed_comment_timestamps, e.g. older payloads pre-dating the
  // field. There's no badge fallback for that case.
  const provenanceHeaderText = showProvenanceHeader ? getProvenanceHeaderText(sortedActions, displayTimeZone) : null

  return (
    <div className={styles.cardList}>
      {provenanceHeaderText && (
        <div className={styles.provenanceHeader} data-test="provenance-header">
          {provenanceHeaderText}
        </div>
      )}
      {sortedActions.map((action) => (
        <AiDraftCard
          key={action.id}
          action={action}
          onDecided={handleCardDecided}
          onFatalDismiss={onFatalDismiss ?? onClose}
          autoFocus={autoFocus}
          renderEditor={renderEditor}
        />
      ))}
    </div>
  )
}

export default AiDraftWorkflowOrchestrator
